/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import PropTypes from 'prop-types'
import React, {Fragment} from 'react'
import {useIntl, FormattedMessage} from 'react-intl'

import {AlertIcon} from '@salesforce/retail-react-app/app/components/icons'
import {
    Alert,
    Button,
    Flex,
    Stack,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'

import LoginFields from '../../components/forms/login-fields'
import {colors} from '../../constants'
import Link from '../link'

const LoginForm = ({
    submitForm,
    clickForgotPassword = noop,
    clickCreateAccount = noop,
    form
}) => {
    const intl = useIntl()

    return (
        <Fragment>
            <Stack justify="center" align="center" marginBottom="40px">
                <Text
                    align="center"
                    fontSize="40px"
                    lineHeight="47px"
                    fontFamily="Raleway"
                    fontWeight="300"
                >
                    <FormattedMessage
                        defaultMessage="Login"
                        id="login_form.message.heading"
                    />
                </Text>
            </Stack>
            <form
                id="login-form"
                onSubmit={form.handleSubmit(submitForm)}
                data-testid="sf-auth-modal-form"
            >
                <Stack spacing="20px">
                    {form.formState.errors?.global && (
                        <Alert status="error">
                            <AlertIcon color="red.500" boxSize={4} />
                            <Text fontSize="sm" ml={3}>
                                {form.formState.errors.global.message}
                            </Text>
                        </Alert>
                    )}
                    <Stack>
                        <LoginFields
                            form={form}
                            clickForgotPassword={clickForgotPassword}
                        />
                    </Stack>
                    <Stack spacing={6}>
                        <Flex
                            justifyContent="space-between"
                            alignItems="center"
                            width="100%"
                            border="1px solid"
                            background="black"
                            backgroundColor="black"
                            borderRadius="20px"
                            padding="0 15px"
                        >
                            <Button
                                type="submit"
                                onClick={() => form.clearErrors('global')}
                                isLoading={form.formState.isSubmitting}
                                borderRadius="20px"
                                display="flex"
                                gap="10px"
                                justifyContent="left"
                                padding="0"
                                fontSize="14px"
                                fontWeight="300"
                                background={colors.black}
                                backgroundColor={colors.black}
                                _hover={{
                                    background: colors.black,
                                    backgroundColor: colors.black
                                }}
                                _active={{
                                    background: colors.black,
                                    backgroundColor: colors.black
                                }}
                                _disabled={{
                                    background: colors.black,
                                    backgroundColor: colors.black
                                }}
                            >
                                <FormattedMessage
                                    defaultMessage="Sign in"
                                    id="login_form.button.sign_in"
                                />
                                <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 14 14"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M1.16663 7H12.8333M12.8333 7L6.99996 1.16667M12.8333 7L6.99996 12.8333"
                                        stroke={colors.white}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </Button>
                            <Link
                                buttonStyles={{
                                    color: colors.white,
                                    fontSize: '14px',
                                    fontWeight: '300',
                                    textDecoration: 'underline',
                                    _hover: {
                                        textDecoration: 'underline'
                                    },
                                    _focus: {
                                        background: colors.black,
                                        backgroundColor: colors.black
                                    },
                                    width: 'fit-content',
                                    background: colors.black,
                                    backgroundColor: colors.black,
                                    height: '15px'
                                }}
                                type="tertiary"
                                text={intl.formatMessage({
                                    defaultMessage: 'Create Account',
                                    id: 'register_form.heading'
                                })}
                                onLinkClick={clickCreateAccount}
                            />
                        </Flex>
                    </Stack>
                </Stack>
            </form>
        </Fragment>
    )
}

LoginForm.propTypes = {
    submitForm: PropTypes.func,
    clickForgotPassword: PropTypes.func,
    clickCreateAccount: PropTypes.func,
    form: PropTypes.object
}

export default LoginForm
